import { Injectable } from '@angular/core';
import { AsyncSubject, Observable, of } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { IAssignedQueue, IQueue } from './queue';
import { MIHttpClientService } from '../../../shared/mi-http-client.service';
import { PermissionService } from '../../../core/permission.service';

@Injectable({
  providedIn: 'root',
})
export class QueueService {

  private apiUrl = environment.apiUrl;

  constructor(
    private miClient: MIHttpClientService,
    private permissionService: PermissionService,
  ) {
  }

  public getQueues(): Observable<any> {
    return this.miClient.get<IQueue[]>('destinations').pipe(
      map(results => results.sort((a, b) => (a.name > b.name) ? 1 : -1)),
    );
  }

  public getQueue(queueId: number): Observable<IQueue> {
    return this.miClient.get(`destinations/${queueId}`);
  }

  public deleteQueue(queueId: number): Observable<any> {
    return this.miClient.delete(`destinations/${queueId}`);
  }

  public saveQueue(queue: IQueue) {
    return this.miClient.post('destinations', queue);
  }

  public updateQueue(queueId: number, queue: IQueue) {
    return this.miClient.post(`destinations/${queueId}`, queue);
  }

  public getAllQueuesWithAssignedInfo(assignedQueuesIds: number[]): Observable<{ items: IAssignedQueue[] }> {
    const assignedQueuesSubject = new AsyncSubject<{ items: IAssignedQueue[] }>();

    this.getQueues().subscribe(queues => {
        const userQueues = queues.map(queue => {
          return {
            queue: queue,
            assigned: assignedQueuesIds && assignedQueuesIds.includes(queue.id),
          };
        });

        assignedQueuesSubject.next({items: userQueues});
        assignedQueuesSubject.complete();
      },
    );

    return assignedQueuesSubject.asObservable();
  }

  public getUserQueues(userId: number): Observable<IQueue[]> {
    return this.getQueuesIfOnePermission(`destinations/user/${userId}`, 'VIBA_ADMIN');
  }

  public assignUserToQueues(userId: number, queueIds: number[]): Observable<any> {
    return this.miClient.post(`destinations/user/${userId}`, queueIds);
  }

  private getQueuesIfOnePermission(apiUrl: string, ...permission: string[]): Observable<IQueue[]> {
    return this.permissionService.hasAtLeastOnePermission(...permission).pipe(
      take(1),
      mergeMap(hasPermission => {
        if (hasPermission) {
          return this.miClient.get<IQueue[]>(apiUrl);
        } else {
          return of([]);
        }
      }),
    );
  }
}
